import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpRight, CalendarCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { DIVISIONS } from "@/data/divisions";
import { api } from "@/lib/api";

const TIME_SLOTS = [
  "09:00",
  "10:00",
  "11:00",
  "12:00",
  "14:00",
  "15:00",
  "16:00",
  "17:00",
];

const Book = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const presetDivision = params.get("division") || "";
  const presetListing = params.get("listing") || "";

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    division: presetDivision,
    service: "",
    preferred_date: "",
    preferred_time: "",
    notes: presetListing ? `Interested in: ${presetListing}` : "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (presetDivision) {
      setForm((s) => ({ ...s, division: presetDivision }));
    }
  }, [presetDivision]);

  const update = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const division = DIVISIONS.find((d) => d.slug === form.division);

  const submit = async (e) => {
    e.preventDefault();
    const required = ["name", "email", "phone", "division", "preferred_date"];
    for (const k of required) {
      if (!form[k]) {
        toast.error(`Please fill in ${k.replace("_", " ")}.`);
        return;
      }
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        division: division ? division.name : form.division,
      };
      await api.post("/appointments", payload);
      toast.success("Appointment requested — we'll confirm shortly.");
      setSuccess(true);
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="pt-40 pb-24 px-6 md:px-12 lg:px-24 min-h-screen flex items-center justify-center bg-white">
        <div
          data-testid="booking-success"
          className="max-w-xl w-full text-center rounded-3xl border border-slate-100 bg-white p-12 shadow-[0_30px_80px_rgba(10,37,64,0.08)]"
        >
          <div className="w-16 h-16 rounded-2xl bg-[#0A2540] text-white flex items-center justify-center mx-auto">
            <CalendarCheck size={28} strokeWidth={1.5} />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-slate-900 mt-6">
            Appointment Requested
          </h1>
          <p className="text-slate-600 leading-relaxed mt-4">
            Thank you, {form.name.split(" ")[0]}! We've received your request
            for <strong className="text-slate-900">{division?.name || form.division}</strong>{" "}
            on {form.preferred_date}{form.preferred_time ? ` at ${form.preferred_time}` : ""}.
            Our team will contact you shortly at {form.email}.
          </p>
          <div className="mt-10 flex gap-3 justify-center">
            <Button
              data-testid="booking-success-home"
              onClick={() => navigate("/")}
              className="rounded-full bg-[#0A2540] text-white hover:bg-[#0F172A] h-12 px-6"
            >
              Back to Home
            </Button>
            <Button
              data-testid="booking-new"
              variant="outline"
              onClick={() => {
                setSuccess(false);
                setForm({
                  name: "",
                  email: "",
                  phone: "",
                  division: "",
                  service: "",
                  preferred_date: "",
                  preferred_time: "",
                  notes: "",
                });
              }}
              className="rounded-full h-12 px-6 border-slate-300 hover:bg-slate-50"
            >
              Book Another
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="book-page" className="pt-32 pb-24 px-6 md:px-12 lg:px-24 bg-white min-h-screen">
      <button
        data-testid="book-back-button"
        onClick={() => navigate(-1)}
        className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
      >
        <ArrowLeft size={14} /> Back
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-10">
        <div className="lg:col-span-5">
          <p className="uppercase text-xs tracking-[0.25em] font-semibold text-[#0284C7]">
            Book an Appointment
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-light text-slate-900 mt-5 leading-[1.05]">
            Schedule with our<br />
            <span className="font-medium text-[#0A2540]">specialists</span>.
          </h1>
          <p className="text-slate-600 leading-relaxed mt-6 max-w-md">
            Choose a division, share a few details and select a preferred date.
            Our team will reach out within one business day to confirm.
          </p>
          <div className="mt-10 space-y-4 text-sm text-slate-600">
            <p>
              <span className="text-slate-400">Hours · </span>
              Mon - Sat, 9:00 - 18:00 IST
            </p>
            <p>
              <span className="text-slate-400">Support · </span>
              ronniemehra69@gmail.com
            </p>
          </div>
        </div>

        <form
          data-testid="booking-form"
          onSubmit={submit}
          className="lg:col-span-7 rounded-3xl border border-slate-100 bg-white p-8 lg:p-12 shadow-[0_8px_30px_rgba(10,37,64,0.04)] space-y-5"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Full Name</label>
              <Input
                data-testid="booking-input-name"
                value={form.name}
                onChange={update("name")}
                placeholder="Your full name"
                className="mt-2 h-12 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-[#0A2540]/20"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Email</label>
              <Input
                data-testid="booking-input-email"
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="you@company.com"
                className="mt-2 h-12 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-[#0A2540]/20"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Phone</label>
              <Input
                data-testid="booking-input-phone"
                value={form.phone}
                onChange={update("phone")}
                placeholder="+91 ..."
                className="mt-2 h-12 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-[#0A2540]/20"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Division</label>
              <Select value={form.division} onValueChange={(v) => setForm((s) => ({ ...s, division: v, service: "" }))}>
                <SelectTrigger
                  data-testid="booking-select-division"
                  className="mt-2 h-12 rounded-xl border-slate-200 focus:ring-2 focus:ring-[#0A2540]/20"
                >
                  <SelectValue placeholder="Select a division" />
                </SelectTrigger>
                <SelectContent>
                  {DIVISIONS.map((d) => (
                    <SelectItem key={d.slug} value={d.slug} data-testid={`booking-division-opt-${d.slug}`}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Service</label>
              <Select value={form.service} onValueChange={(v) => setForm((s) => ({ ...s, service: v }))} disabled={!division}>
                <SelectTrigger
                  data-testid="booking-select-service"
                  className="mt-2 h-12 rounded-xl border-slate-200 focus:ring-2 focus:ring-[#0A2540]/20"
                >
                  <SelectValue placeholder={division ? "Select a service" : "Select division first"} />
                </SelectTrigger>
                <SelectContent>
                  {division?.services.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Preferred Date</label>
              <Input
                data-testid="booking-input-date"
                type="date"
                value={form.preferred_date}
                onChange={update("preferred_date")}
                min={new Date().toISOString().split("T")[0]}
                className="mt-2 h-12 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-[#0A2540]/20"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Preferred Time</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {TIME_SLOTS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    data-testid={`booking-time-${t}`}
                    onClick={() => setForm((s) => ({ ...s, preferred_time: t }))}
                    className={`px-4 py-2.5 rounded-full text-sm border transition-all ${
                      form.preferred_time === t
                        ? "bg-[#0A2540] text-white border-[#0A2540]"
                        : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Notes (optional)</label>
            <Textarea
              data-testid="booking-input-notes"
              value={form.notes}
              onChange={update("notes")}
              placeholder="Anything we should know..."
              className="mt-2 min-h-[120px] rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-[#0A2540]/20"
            />
          </div>
          <Button
            data-testid="booking-submit-button"
            type="submit"
            disabled={loading}
            className="rounded-full bg-[#0A2540] hover:bg-[#0F172A] text-white h-12 px-7"
          >
            {loading ? "Submitting..." : "Confirm Appointment"}
            <ArrowUpRight size={16} strokeWidth={1.5} className="ml-1" />
          </Button>
        </form>
      </div>
    </div>
  );
};

export default Book;
